/**
 * Headshot Component
 */

import React, { FC, useMemo } from 'react';
import { HeadshotProps, HeadshotTypes } from './headshot.types';
import { HeadshotSkeleton } from './headshot.style';
import Image from '../Image/image';
import aspectRatioPercentage from '../../utils/aspectRatioPercentage';
import COLORS from '../../style/universal.color';

const AMP = '&';
const ONE_BY_ONE = '1:1';

const HeadshotTypeAspectRatios = {
  [HeadshotTypes.DEFAULT]: ONE_BY_ONE,
  [HeadshotTypes.SILO]: ONE_BY_ONE,
  [HeadshotTypes.SPOTS]: ONE_BY_ONE,
  [HeadshotTypes.MILB]: '2:3',
  [HeadshotTypes.SIXTY_SEVEN]: '2:3',
  [HeadshotTypes.EIGHTY_THREE]: '24:29'
};

const sizeRange = [30, 60, 90, 120, 150, 180, 240];

export const HeadshotFallback: string = 'https://prod-gameday.mlbstatic.com/responsive-gameday-assets/1.3.0/images/players/default-batter.svg';

const generateUrl = (scaling: number, playerId: string | number, zoomLevel: number, width: number, type: HeadshotTypes, asCircle: boolean) => {
  const headshotWidth = width * scaling;
  let query = [];
  if (zoomLevel > 1) {
    query.push(`zoom=${zoomLevel}`);
  }
  if (asCircle) {
    query.push('circle=true');
  }
  return `https://midfield.mlbstatic.com/v1/people/${playerId}/${type}/${headshotWidth}${query.length ? '?' + query.join(AMP) : ''}`;
}

// want to keep to a range of acceepted sizes to keep asset costs down
const generateRoundedSize = (width: number | string) => {
  const rangeLength = sizeRange.length;
  let headshotWidth = sizeRange[rangeLength - 1];
  const widthNum = typeof width === 'string' ? width.replace('px', '') : width;
  for (let i = 0; i < rangeLength; i++) {
    if (sizeRange[i] >= widthNum) {
      headshotWidth = sizeRange[i];
      break;
    }
  }

  return headshotWidth;
};

export const constructHeadshotURLSrcset = (playerId: string | number, zoomLevel: number, width: string | number, type: HeadshotTypes, asCircle: boolean = false) => {
  // set img to appropriate width
  // TODO what if percentage?
  let headshotWidth = generateRoundedSize(width);

  return {
    '1x': generateUrl(1, playerId, zoomLevel, headshotWidth, type, asCircle),
    '2x': generateUrl(2, playerId, zoomLevel, headshotWidth, type, asCircle),
    '3x': generateUrl(3, playerId, zoomLevel, headshotWidth, type, asCircle)
  }
};

const Headshot: FC<HeadshotProps> = (props: HeadshotProps) => {
  const {
    src = '',
    srcset,
    playerId,
    rounded = true,
    type = HeadshotTypes.DEFAULT,
    errorImageSrc = HeadshotFallback,
    isLoading,
    width = '60px',
    zoomScale = 1.2,
  } = props;

  const { ratio = (HeadshotTypeAspectRatios[type] || HeadshotTypeAspectRatios[HeadshotTypes.DEFAULT]) } = props;
  const isSilo = type === HeadshotTypes.SILO;
  const isSpots = type === HeadshotTypes.SPOTS;
  const isOneByOne = ( ratio == ONE_BY_ONE );

  let { closeUp, backgroundColor = (isSilo ? COLORS.mlbGrey1: undefined) } = props;

  let borderRadius;

  if ( !isOneByOne ) {
    closeUp = closeUp === true;
    borderRadius = '8px';
  } else {
    closeUp = closeUp !== false;
    borderRadius = '50%';
  }

  if ( isLoading ) {
    const skeletonRatio:number = aspectRatioPercentage(ratio);

    return (
      <HeadshotSkeleton width={ width } ratio={ skeletonRatio } isCircle={ rounded && isOneByOne } borderRadius={ borderRadius } />
    );
  }

  const zoomLevel = closeUp ? zoomScale : 1;

  let providedSrcset = srcset;

  // If user has passed a playerId, give that precedence and build the srcset.
  if (playerId != undefined) {
    providedSrcset = useMemo(() => {
      //  If the user is asking for 1:1, and an image type isn't an implicit 1:1 (silo, spots)
      const asCircle = !(isSpots || isSilo) && isOneByOne;
      return constructHeadshotURLSrcset(playerId, zoomLevel, width, type as HeadshotTypes, asCircle);
    }, [playerId, zoomLevel, width, type, isOneByOne, props.ratio]);
  }

  const providedSrc: string = playerId != undefined  && providedSrcset && providedSrcset['1x'] || src;

  return (
    <Image
      { ...props }
      key={ playerId }
      src={ providedSrc }
      srcset={ providedSrcset }
      ratio={ ratio }
      rounded={ isOneByOne && rounded }
      borderRadius={ rounded ? borderRadius : undefined}
      errorImageSrc={ errorImageSrc }
      backgroundColor={ backgroundColor }
      sizes={ typeof width === 'number' ? `${width}px` : width }
    />
  );
};

export default Headshot;
export { HeadshotTypes };
